import { supabase } from '@/integrations/supabase/client';

export type ErrorSeverity = 'info' | 'warning' | 'error' | 'critical';

interface LogErrorParams {
  errorCode: string;
  title: string;
  message: string;
  technicalDetails?: string;
  severity?: ErrorSeverity;
  route?: string;
  metadata?: Record<string, any>;
}

// Generate session ID (persists across page reloads)
function getSessionId(): string {
  const storageKey = 'app-session-id';
  let sessionId = sessionStorage.getItem(storageKey);
  
  if (!sessionId) {
    sessionId = `sess-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    sessionStorage.setItem(storageKey, sessionId);
  }
  
  return sessionId;
}

// Get previous route from navigation history
function getPreviousRoute(): string | null {
  try {
    const navHistory = sessionStorage.getItem('nav-history');
    if (navHistory) {
      const history = JSON.parse(navHistory);
      return history[history.length - 2] || null;
    }
  } catch (e) {
    // Ignore
  }
  return null;
}

// Track navigation for error context
export function trackNavigation(route: string) {
  try {
    const storageKey = 'nav-history';
    const existing = sessionStorage.getItem(storageKey);
    const history = existing ? JSON.parse(existing) : [];
    
    // Keep last 10 routes
    history.push(route);
    if (history.length > 10) {
      history.shift();
    }
    
    sessionStorage.setItem(storageKey, JSON.stringify(history));
  } catch (e) {
    // Ignore storage errors
  }
}

// Generate dedupe key for similar errors
function generateDedupeKey(title: string, message: string, route?: string): string {
  const baseString = `${title}:${message}:${route || 'unknown'}`;
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < baseString.length; i++) {
    const char = baseString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return `dedupe-${Math.abs(hash).toString(36)}`;
}

// Get browser/device info
function getBrowserInfo() {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    online: navigator.onLine,
    cookieEnabled: navigator.cookieEnabled,
  };
}

/**
 * Log an error to the database
 */
export async function logError({
  errorCode,
  title,
  message,
  technicalDetails,
  severity = 'error',
  route,
  metadata = {},
}: LogErrorParams): Promise<number | null> {
  try {
    const currentRoute = route || window.location.pathname;
    const previousRoute = getPreviousRoute();
    const sessionId = getSessionId();
    const dedupeKey = generateDedupeKey(title, message, currentRoute);
    
    // Build payload with all context
    const payload = {
      title,
      message,
      technicalDetails,
      browser: getBrowserInfo(),
      timestamp: new Date().toISOString(),
      url: window.location.href,
      ...metadata,
    };

    // Call the database function
    const { data, error } = await supabase.rpc('log_client_error' as any, {
      p_error_code: errorCode,
      p_severity: severity,
      p_summary: `${title}: ${message}`,
      p_payload: payload,
      p_route_current: currentRoute,
      p_route_previous: previousRoute,
      p_session_id: sessionId,
      p_correlation_id: null,
      p_dedupe_key: dedupeKey,
      p_app_name: 'FaceFable',
      p_app_version: '1.0.0',
      p_platform: 'web',
    });

    if (error) {
      console.error('Failed to log error to database:', error);
      return null;
    }

    return data as number;
  } catch (err) {
    // Don't let error logging break the app
    console.error('Error logger failed:', err);
    return null;
  }
}

/**
 * Log a React error boundary error
 */
export async function logReactError(
  error: Error,
  errorInfo: { componentStack?: string },
  errorCode: string
): Promise<number | null> {
  return logError({
    errorCode,
    title: 'React Error',
    message: error.message,
    technicalDetails: `${error.stack}\n\nComponent Stack:\n${errorInfo.componentStack || 'N/A'}`,
    severity: 'critical',
    metadata: {
      errorName: error.name,
      componentStack: errorInfo.componentStack,
    },
  });
}

/**
 * Log a global unhandled error
 */
export async function logUnhandledError(
  event: ErrorEvent
): Promise<number | null> {
  return logError({
    errorCode: `ERR-UNHANDLED-${Date.now().toString(36).toUpperCase()}`,
    title: 'Unhandled Error',
    message: event.message,
    technicalDetails: `${event.error?.stack || 'No stack trace'}`,
    severity: 'critical',
    metadata: {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    },
  });
}

/**
 * Log an unhandled promise rejection
 */
export async function logUnhandledRejection(
  event: PromiseRejectionEvent
): Promise<number | null> {
  const reason = event.reason;
  const message = reason instanceof Error ? reason.message : String(reason);
  const stack = reason instanceof Error ? reason.stack : undefined;

  return logError({
    errorCode: `ERR-PROMISE-${Date.now().toString(36).toUpperCase()}`,
    title: 'Unhandled Promise Rejection',
    message,
    technicalDetails: stack || 'No stack trace available',
    severity: 'error',
    metadata: {
      reason: String(reason),
    },
  });
}

// Set up global error handlers
export function setupGlobalErrorHandlers() {
  // Unhandled errors
  window.addEventListener('error', (event) => {
    logUnhandledError(event);
  });

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    logUnhandledRejection(event);
  });

  // Track navigation
  const originalPushState = window.history.pushState;
  window.history.pushState = function(...args) {
    const result = originalPushState.apply(this, args);
    trackNavigation(window.location.pathname);
    return result;
  };
}
